import React, { useState, useEffect, useRef } from 'react';
import { Select, Toast, Button, Avatar } from '@douyinfe/semi-ui';
import { IconUser } from '@douyinfe/semi-icons';
import request from '../../utils/request';

const OBJ_TYPE_CONFIG = {
    0: {
        label: '用户',
        api: '/go/sp/su/listmini_service_user/',
        avatarKey: 'avatar',
        nameKey: 'name',
        extra: null,
    },
    1: {
        label: '员工',
        api: '/go/sp/o2o/listmini_staff_user/',
        avatarKey: 'avatar',
        nameKey: 'name',
        extra: (item) => item.title ? `(${item.title})` : '',
    },
    2: {
        label: '子账号',
        api: '/go/sp/subsp/listmini_sub_sp/',
        avatarKey: '',
        nameKey: 'name',
        extra: null,
    },
    10: {
        label: '分类',
        api: '/go/sp/shop/mlist_product_cat/',
        avatarKey: '',
        nameKey: 'name',
        extra: null,
    },
    11: {
        label: '门店',
        api: '/go/sp/shop/mlist_store/',
        avatarKey: '',
        nameKey: 'name',
        extra: null,
    },
    // 后续可扩展
};

const ObjSelector = ({ obj_type = 0, value, onChange, placeholder, disabled, single = true, style }) => {
    const config = OBJ_TYPE_CONFIG[obj_type] || OBJ_TYPE_CONFIG[0];
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 20, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [internalValue, setInternalValue] = useState([]);
    const searchTimeoutRef = useRef(null);
    const requestedUidsRef = useRef(new Set());

    // 获取详情（用于已选中的项）
    const fetchDetails = async (uids) => {
        try {
            const response = await request.get(config.api, {
                params: { uids: uids.join(',') }
            });
            if (response.errno === 0 && response.list) {
                setList(prevList => {
                    const newList = [...prevList];
                    response.list.forEach(item => {
                        if (!newList.some(i => i.uid === item.uid)) {
                            newList.push(item);
                        }
                    });
                    return newList;
                });
            }
        } catch { }
    };

    // 获取列表
    const fetchList = async (selectedUids = []) => {
        if (!selectedUids || !selectedUids.length) {
            selectedUids = Array.isArray(value) ? value : [value];
        }

        setLoading(true);
        try {
            // 先查已选详情
            let selectedItems = [];
            if (Array.isArray(selectedUids) && selectedUids.length > 0) {
                const selectedResponse = await request.get(config.api, {
                    params: { uids: selectedUids.join(',') }
                });
                if (selectedResponse.errno === 0 && selectedResponse.list) {
                    selectedItems = selectedResponse.list;
                }
            }
            // 查搜索结果
            const response = await request.get(config.api, {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    key: searchKey,
                    status: -1
                }
            });
            if (response.errno === 0) {
                const searchResults = response.list || [];
                const mergedList = [...searchResults];
                selectedItems.forEach(item => {
                    if (!mergedList.some(i => i.uid === item.uid)) {
                        mergedList.push(item);
                    }
                });
                console.log('mergedList', selectedItems, selectedUids, internalValue, value, mergedList);
                setList(mergedList);
                setPagination(prev => ({ ...prev, total: response.total_cnt }));
            } else {
                Toast.error(response.errstr || `获取${config.label}列表失败`);
            }
        } catch (error) {
            Toast.error(`获取${config.label}列表失败`);
        } finally {
            setLoading(false);
        }
    };

    // value变化时，补全详情
    useEffect(() => {
        if (value) {
            const valueArray = Array.isArray(value) ? value : [value];
            const validValues = valueArray.filter(v => v && v !== '0');
            setInternalValue(validValues);
            const missing = validValues.filter(uid => !list.some(i => i.uid === uid));
            if (missing.length > 0) fetchDetails(missing);
        } else {
            setInternalValue([]);
        }
    }, [value]);

    // 搜索/分页时拉取
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            fetchList(internalValue);
        }, 300);
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchKey, pagination.page]);

    const handleChange = (selectedUids) => {
        setInternalValue(selectedUids);
        onChange?.(selectedUids);
    };

    const renderCustomOption = (item) => {
        return (
            <Select.Option key={item.uid} value={item.uid} showTick={true} style={{ display: 'flex', paddingLeft: 24, paddingTop: 10, paddingBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar size="small" src={item[config.avatarKey]} style={{ flexShrink: 0 }}>{item[config.nameKey]?.[0]}</Avatar>
                    <div style={{ fontSize: 14 }}>{item[config.nameKey]}</div>
                    {config.extra && <div style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>{config.extra(item)}</div>}
                </div>
            </Select.Option>
        );
    };

    // 新增：用于补全单个uid的详情
    const getItemByUid = async (uid) => {
        if (!uid) return null;
        if (Array.isArray(uid)) {
            uid.forEach(async (u) => {
                await getItemByUid(u);
            });
            return;
        }

        let item = list.find(i => i.uid === uid);
        if (!item) {
            try {
                const response = await request.get(config.api, { params: { uids: uid } });
                if (response.errno === 0 && response.list && response.list.length > 0) {
                    setList(prev => {
                        // 防止重复
                        const exists = prev.some(i => i.uid === uid);
                        return exists ? prev : [...prev, ...response.list];
                    });
                    return response.list[0];
                }
            } catch (e) {
                console.log('[ObjSelector] getItemByUid error', e);
            }
        }
        return item;
    };

    return (
        <Select
            loading={loading}
            value={internalValue}
            onChange={handleChange}
            style={style || { width: '100%' }}
            placeholder={placeholder || (single ? `请选择${config.label}` : `请选择${config.label}（多选）`)}
            filter
            searchPosition='dropdown'
            showClear
            multiple={!single ? true : false}
            maxTagCount={!single ? 3 : 1}
            filterOption={(input, option) => {
                const name = option?.label?.toString() || '';
                return name.toLowerCase().includes(input.toLowerCase());
            }}
            prefix={<IconUser style={{ color: 'var(--semi-color-primary)' }} />}
            disabled={disabled}
            onDropdownVisibleChange={(visible) => {
                if (visible) {
                    setSearchKey('');
                    setPagination(prev => ({ ...prev, page: 0 }));
                    fetchList(internalValue);
                }
            }}
            onSearch={(value) => {
                setSearchKey(value);
                setPagination(prev => ({ ...prev, page: 0 }));
            }}
            outerBottomSlot={
                pagination.total > (pagination.page + 1) * pagination.limit && (
                    <Button
                        type="tertiary"
                        onClick={() => {
                            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                        }}
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        加载更多
                    </Button>
                )
            }
            // 新增：自定义选中项显示
            renderSelectedItem={(option, { value } = {}) => {
                let realValue = value;
                if (realValue === undefined && option && option.value !== undefined) {
                    realValue = option.value;
                }
                if (!realValue && realValue !== 0) return '-';
                const strValue = String(realValue);
                const item = list.find(i => String(i.uid) === strValue);
                if (item) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Avatar size="small" src={item[config.avatarKey]} style={{ flexShrink: 0 }}>{item[config.nameKey]?.[0]}</Avatar>
                            <span>{item[config.nameKey]}</span>
                            {config.extra && <span style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>{config.extra(item)}</span>}
                        </div>
                    );
                } else {
                    if (!requestedUidsRef.current.has(strValue)) {
                        requestedUidsRef.current.add(strValue);
                        getItemByUid(realValue);
                    }
                    //return strValue;
                    return '';
                }
            }}
        >
            {list.map(item => renderCustomOption(item))}
        </Select>
    );
};

export default ObjSelector; 
